/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import ClientsContext from './ClientsContext';
import initialClientsState from './initialClientsState';
import userReducer from 'contexts/clients/reducer/clients.reducer';
import * as action from 'contexts/clients/reducer/clients.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IaddNewExchangeRateProps,
  IClientState,
  IcreatePointOfSaleProps,
  IPointOfSaleState,
  IselectClientActionProps,
  IselectPointOfSaleActionProps,
  IUpdateAliasPointOfSaleProps,
  IupdateExchangeRateProps,
  TClientToUpdateRequest,
  TDataUpgrade,
  TPointsOfSaleToUpdateRequest,
} from 'interfaces/clients.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';
import { IChildrenProps, matching } from 'interfaces/general.interface';
import {
  IcreateClientAliasAPIBody,
  IupdateClientAPIBody,
} from 'api/endpoints/clients/clients.response.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function ClientsProvider(props: IChildrenProps) {
  const [state, dispatch] = useReducer(userReducer, initialClientsState);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();
  const { exchanges, countries, associationStates } = useContextGeneral();

  useEffect(() => {
    (async () => {
      if (
        !company?.id ||
        countries.length === 0 ||
        exchanges.length === 0 ||
        !associationStates?.connected?.id
      ) {
        return;
      }
      const token = await getAccessTokenSilently();
      if (!token) return;
      return await action.getClientsAction({
        dispatch,
        tenantId: company.id,
        associationStates,
        exchanges,
        countries,
        token,
        translate,
      });
    })();
  }, [company?.id, associationStates, exchanges, countries]);

  useEffect(() => {
    if (state.segmentations && state.segmentations?.segmentation?.length > 0) return;
    (async () => {
      if (isAuthenticated && company?.brand?.id) {
        const token = await getAccessTokenSilently();
        if (!token) return;
        return await action.getSegmentationsAction({
          dispatch,
          brandId: company.brand.id,
          translate,
          token,
        });
      }
    })();
  }, [company?.brand?.id, isAuthenticated]);

  const getClients = useCallback(async () => {
    if (
      !company?.id ||
      countries.length === 0 ||
      exchanges.length === 0 ||
      !associationStates?.connected?.id
    ) {
      return false;
    }
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getClientsAction({
      dispatch,
      tenantId: company.id,
      associationStates,
      exchanges,
      countries,
      token,
      translate,
    });
  }, [company?.id, associationStates, exchanges, countries]);

  const selectClient = useCallback(
    async ({ clientId }: IselectClientActionProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const client = state.clients.find((client) => client.key === clientId);
      if (!client) return false;
      client.enableModify = client.status === matching ? true : false;
      return await action.selectClientAction({
        dispatch,
        client,
        translate,
        token,
      });
    },
    [state.clients]
  );

  const selectPointOfSale = useCallback(
    ({ pointOfSale }: IselectPointOfSaleActionProps) => {
      return action.selectPointOfSaleAction({ dispatch, pointOfSale });
    },
    []
  );

  const createClient = useCallback(
    async ({ client }: { client: IcreateClientAliasAPIBody }) => {
      if (countries.length === 0) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createClientAction({
        dispatch,
        client,
        token,
        translate,
      });
    },
    [countries.length]
  );

  const updateClient = useCallback(
    async ({ client }: { client: Partial<IupdateClientAPIBody> }) => {
      if (!state.clientSelected?.key) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateClientAction({
        dispatch,
        clientId: state.clientSelected.key,
        client,
        token,
        translate,
      });
    },
    [state.clientSelected?.key]
  );

  const deleteClient = useCallback(
    async ({ clientAliasId }: { clientAliasId: string }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deleteClientAction({
        dispatch,
        clients: state.clients,
        clientAliasId,
        token,
        translate,
      });
    },
    [state.clients]
  );

  const createPointOfSale = useCallback(
    async ({ pointOfSale }: { pointOfSale: IcreatePointOfSaleProps }) => {
      if (countries.length === 0 || !state?.clientSelected || !state.segmentations) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createPointOfSaleAction({
        dispatch,
        clientSelected: state.clientSelected,
        segmentationsState: state.segmentations,
        pointOfSale,
        countries,
        token,
        translate,
      });
    },
    [state?.clientSelected, state.segmentations, countries]
  );

  const updatePointOfSale = useCallback(
    async (pointOfSale: IUpdateAliasPointOfSaleProps) => {
      if (
        !company?.id ||
        countries.length === 0 ||
        !state.clientSelected ||
        !state.segmentations
      ) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updatePointOfSaleAction({
        dispatch,
        tenantId: company.id,
        clientSelected: state.clientSelected,
        segmentationsState: state.segmentations,
        pointOfSale,
        countries,
        token,
        translate,
      });
    },
    [company?.id, state.segmentations, state.clientSelected, countries]
  );

  const removePointOfSale = useCallback(
    async (pointOfSale: IPointOfSaleState) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removePointOfSaleAction({
        dispatch,
        pointOfSale,
        token,
        translate,
      });
    },
    [state]
  );

  const upgradePointsOfSale = useCallback(
    async (fieldsToUpgrade: TDataUpgrade[], clientId: string) => {
      if (!company?.id || countries.length === 0 || !state.clientSelected) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.upgradePointsOfSaleAction({
        dispatch,
        tenantId: company.id,
        clientSelected: state.clientSelected,
        clientsState: state.clients,
        pointsOfSaleState: state.pointsOfSale,
        countries,
        fieldsToUpgrade,
        clientId,
        token,
        translate,
      });
    },
    [company?.id, state.clientSelected, state.clients, state.pointsOfSale, countries]
  );

  const shareDataPointOfSale = useCallback(
    async (pointOfSaleId: string, haveToShare: boolean) => {
      if (!state.clientSelected) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.shareDataPointOfSaleAction({
        dispatch,
        clientSelected: state.clientSelected,
        pointOfSaleId,
        haveToShare,
        token,
        translate,
      });
    },
    [state.clientSelected]
  );

  const searchClients = useCallback(
    (query: string) => {
      return action.searchClientsAction({ dispatch, state, query });
    },
    [state]
  );

  const searchPointsOfSale = useCallback(
    (query: string) => {
      return action.searchPointsOfSaleAction({ dispatch, state, query });
    },
    [state]
  );

  const acceptAssociation = useCallback(
    async (
      data: TClientToUpdateRequest,
      status: string,
      setIsOpenDrawer: any,
      setCurrentStep: any
    ) => {
      if (
        !company?.id ||
        !associationStates ||
        countries.length === 0 ||
        exchanges.length === 0
      ) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.acceptAssociationAction({
        dispatch,
        clients: state.clients,
        exchanges,
        tenantId: company.id,
        data,
        statusType: status,
        associationStates,
        countries,
        setIsOpenDrawer,
        setCurrentStep,
        token,
        translate,
      });
    },
    [company?.id, associationStates, exchanges, state.clients, countries]
  );

  const rejectAssociation = useCallback(
    async (data: IClientState) => {
      if (
        !company?.id ||
        !associationStates ||
        countries.length === 0 ||
        exchanges.length === 0
      ) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.rejectAssociationAction({
        dispatch,
        data,
        exchanges,
        tenantId: company.id,
        associationStates,
        countries,
        token,
        translate,
      });
    },
    [company?.id, associationStates, exchanges, countries]
  );

  const associatePointsOfSale = useCallback(
    async (
      data: TPointsOfSaleToUpdateRequest[],
      setPointsOfSaleToUpdate: any,
      setIsOpenDrawer: any,
      setCurrentStep: any
    ) => {
      if (
        !company?.id ||
        !associationStates ||
        countries.length === 0 ||
        exchanges.length === 0 ||
        !state.clientSelected
      ) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.associatePointsOfSaleAction({
        dispatch,
        clientSelected: state.clientSelected,
        tenantId: company.id,
        data,
        associationStates,
        countries,
        setPointsOfSaleToUpdate,
        setIsOpenDrawer,
        setCurrentStep,
        token,
        translate,
        exchanges,
      });
    },
    [company?.id, associationStates, state.clientSelected, countries, exchanges]
  );

  const rejectPointOfSale = useCallback(
    async (data: IPointOfSaleState) => {
      if (!state.clientSelected) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.rejectPointOfSaleAction({
        dispatch,
        data,
        clientSelected: state.clientSelected,
        token,
        translate,
      });
    },
    [state.clientSelected]
  );

  const addNewExchangeRate = useCallback(
    async ({ pointOfSaleId, newExchangeRates }: IaddNewExchangeRateProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.addNewExchangeRateAction({
        dispatch,
        pointOfSaleId,
        newExchangeRates,
        token,
        translate,
      });
    },
    []
  );

  const updateExchangeRate = useCallback(
    async ({ exchangeRate }: IupdateExchangeRateProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateExchangeRateAction({
        dispatch,
        exchangeRate,
        token,
        translate,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      getClients,
      selectClient,
      selectPointOfSale,
      createClient,
      updateClient,
      deleteClient,
      createPointOfSale,
      updatePointOfSale,
      removePointOfSale,
      upgradePointsOfSale,
      shareDataPointOfSale,
      searchClients,
      searchPointsOfSale,
      acceptAssociation,
      rejectAssociation,
      associatePointsOfSale,
      rejectPointOfSale,
      addNewExchangeRate,
      updateExchangeRate,
    }),
    [
      state,
      getClients,
      selectClient,
      selectPointOfSale,
      createClient,
      updateClient,
      deleteClient,
      createPointOfSale,
      updatePointOfSale,
      removePointOfSale,
      upgradePointsOfSale,
      shareDataPointOfSale,
      searchClients,
      searchPointsOfSale,
      acceptAssociation,
      rejectAssociation,
      associatePointsOfSale,
      rejectPointOfSale,
      addNewExchangeRate,
      updateExchangeRate,
    ]
  );

  return (
    <ClientsContext.Provider value={memoProvider}>
      {props.children}
    </ClientsContext.Provider>
  );
}

export default memo(ClientsProvider);
